import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import client, { apiErr } from "../lib/api";
import { Spinner, StatCard } from "../components/ui-kit";
import { LayoutDashboard, Users, Wallet, Handshake, Contact, Radio, Sparkles, LogOut, LineChart, BookOpen, MessageCircle, ArrowLeft } from "lucide-react";
import { PushToggle } from "../components/PushToggle";
import { toast } from "sonner";
import TeamClients from "../components/team/TeamClients";
import TeamEarnings from "../components/team/TeamEarnings";
import TeamBrokers from "../components/team/TeamBrokers";
import TeamLeads from "../components/team/TeamLeads";
import { SignalFeed } from "../components/hub/SignalFeed";
import { ChartFeed } from "../components/hub/ChartFeed";
import { Blog } from "../components/hub/Blog";
import { AIChat } from "../components/hub/AIChat";
import MarketingStudio from "../components/team/MarketingStudio";
import { money } from "../components/team/shared";
import { TelegramConnect } from "../components/team/TelegramConnect";
import { MenuTiles, VIP_TILE, SeeMore } from "../components/hub/MenuTiles";
import { Panel } from "../components/ui-kit";
import { MobileTabBar } from "../components/MobileTabBar";
import { MyZone } from "../components/team/MyZone";
import { DutyToggle } from "../components/team/DutyToggle";

const MyZoneSlot = ({ tabs }) => (tabs.some((t) => t.k === "leads") ? <MyZone /> : null);

const TABS = [
  { k: "overview", l: "Overview", short: "Home", icon: LayoutDashboard },
  { k: "signals", l: "Signals", icon: Radio, always: true, d: "Sabhi Gold & Bitcoin signals" },
  { k: "clients", l: "My Clients", icon: Users, d: "Client add & track karein" },
  { k: "earnings", l: "Earnings", icon: Wallet, d: "Commission summary" },
  { k: "brokers", l: "Broker Links", icon: Handshake, d: "Apne referral links" },
  { k: "leads", l: "Leads & Follow-ups", short: "Leads", icon: Contact, d: "Assigned leads" },
  { k: "charts", l: "Chart Analysis", short: "Charts", icon: LineChart, always: true, d: "AI chart breakdown" },
  { k: "blog", l: "Blog", icon: BookOpen, always: true, d: "Analysis & education" },
  { k: "chat", l: "Fateh AI Chat", short: "AI Chat", icon: MessageCircle, always: true, d: "Kuch bhi puchiye" },
  { k: "studio", l: "Marketing Studio", icon: Sparkles, d: "Marketing images banayein" },
];

function Overview({ me, go, tabs }) {
  const s = me.stats;
  return (
    <div className="space-y-6">
      <Panel title="Signals" testid="tm-signals-preview" right={<SeeMore onClick={() => go("signals")} testid="tm-signals-see-more" />}>
        <div className="p-3"><SignalFeed compact limit={2} /></div>
      </Panel>
      <MyZoneSlot tabs={tabs} />
      <MenuTiles items={[...tabs.filter((t) => t.k !== "overview"), VIP_TILE]} onPick={go} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="ov-clients" label="Clients" value={s.clients} />
        <StatCard testid="ov-funding" label="Total Funding" value={money(s.funding)} accent="gold" />
        <StatCard testid="ov-earnings" label="Total Earnings" value={money(s.earnings)} accent="bull" />
        <StatCard testid="ov-leads" label="My Leads" value={s.leads} />
      </div>
      <TelegramConnect connected={me.telegram_connected} />
      <div className="surface p-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[color:var(--gold)] opacity-10 blur-3xl" />
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Your commission share</div>
        <div className="font-mono text-4xl text-gold mb-2">{me.share_pct}%</div>
        <p className="text-sm text-slate-400 max-w-xl">Har client ka MT5 ID sahi daalein — admin jab broker commission report upload karega, earnings MT5 ID se match hokar aapke account me add hongi.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {me.tools.includes("clients") && <button data-testid="ov-go-clients" onClick={() => go("clients")} className="btn-gold px-4 py-2 text-xs">Add a Client</button>}
          {me.tools.includes("studio") && <button data-testid="ov-go-studio" onClick={() => go("studio")} className="px-4 py-2 text-xs border border-gold text-gold hover:bg-[color:var(--gold)] hover:text-black transition-colors duration-200">Create Marketing Image</button>}
        </div>
      </div>
    </div>
  );
}

export default function TeamPortal() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  useEffect(() => { client.get("/tm/me").then(({ data }) => setMe(data)).catch((e) => toast.error(apiErr(e))); }, [tab]);
  if (!me) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--app-bg)" }}><Spinner label="Loading portal" /></div>;
  const tabs = TABS.filter((t) => t.k === "overview" || t.always || me.tools.includes(t.k));
  const views = {
    overview: <Overview me={me} go={setTab} tabs={tabs} />, clients: <TeamClients />, earnings: <TeamEarnings />, brokers: <TeamBrokers />,
    leads: <TeamLeads />, signals: <SignalFeed />, charts: <ChartFeed />, blog: <Blog />, chat: <AIChat />, studio: <MarketingStudio limit={me.image_limit} />,
  };
  return (
    <div className="min-h-screen grain" style={{ background: "var(--app-bg)" }} data-testid="team-portal">
      <header className="sticky top-0 z-20 glass border-b border-[color:var(--border-subtle)]">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          {tab !== "overview" && <button onClick={() => setTab("overview")} data-testid="tm-back-home" className="text-slate-300 hover:text-gold transition-colors duration-200 -ml-1 p-1"><ArrowLeft size={20} /></button>}
          <div className="w-8 h-8 flex items-center justify-center bg-gold text-black font-head font-bold text-sm">F</div>
          <div className="leading-tight">
            <div className="font-head font-bold text-white">Fateh FX</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Partner Portal</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {tabs.some((t) => t.k === "leads") && <DutyToggle />}
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm text-white" data-testid="tm-name">{user?.name}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Team Partner</div>
            </div>
            <PushToggle compact />
            <button data-testid="tm-logout-btn" onClick={logout} className="p-2 text-slate-400 hover:text-bear"><LogOut size={18} /></button>
          </div>
        </div>
      </header>
      <main className="max-w-screen-2xl mx-auto p-4 pb-24 md:p-6">
        <h1 className="font-head text-2xl sm:text-3xl text-white mb-5 tracking-tight">{tabs.find((t) => t.k === tab)?.l || "Overview"}</h1>
        {views[tabs.some((t) => t.k === tab) ? tab : "overview"]}
      </main>
      <MobileTabBar tabs={tabs} primary={["overview", "signals", tabs.some((t) => t.k === "leads") ? "leads" : "charts", "chat"]} active={tab} onPick={(k) => { setTab(k); window.scrollTo(0, 0); }} extra={[VIP_TILE]} />
    </div>
  );
}
